import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Collapse from '@mui/material/Collapse'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import SearchIcon from '@mui/icons-material/Search'
import InputAdornment from '@mui/material/InputAdornment'
import RecordingPlayer from '../components/RecordingPlayer'
import CallResponseViewer from '../components/CallResponseViewer'
import { OUTCOME_COLORS } from '../components/channelColors'
import { apiConstants, postGeneric } from '../../../common/constants'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../../redux/commonApiCalls'
import CopyIcon from '../../../common/CopyIcon'

function RecordingRow({ row }) {
  const [open, setOpen] = useState(false)
  const outcomeColor = OUTCOME_COLORS[row.outcome] || '#607D8B'

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell padding='checkbox'>
          <IconButton size='small' onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.awbNumber}<CopyIcon text={row.awbNumber} /></TableCell>
        <TableCell>{row.merchantName}</TableCell>
        <TableCell>{row.callStartTime ? new Date(row.callStartTime).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</TableCell>
        <TableCell align='right'>{row.callDuration != null ? (() => { const v = Number(row.callDuration); const m = Math.floor(v); const s = Math.round((v - m) * 60); return m > 0 && s > 0 ? `${m}m ${s}s` : m > 0 ? `${m}m` : `${s}s`; })() : '—'}</TableCell>
        <TableCell>
          <Chip label={row.result || row.outcome} size='small' sx={{ bgcolor: `${outcomeColor}18`, color: outcomeColor, fontWeight: 700 }} />
        </TableCell>
        <TableCell>{row.provider}</TableCell>
        <TableCell align='center'>{row.attemptCount}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box sx={{ py: 2, pl: 6, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <RecordingPlayer recordingUrl={row.recordingUrl} duration={row.callDuration} />
              <CallResponseViewer callResponses={row.callResponses} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default function CallRecordings() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)

  const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()

  // Real data — NDR Futwork call recordings (ndr/recording/search), server-paginated.
  useEffect(() => {
    let on = true
    const payload = { offset: page * rowsPerPage, limit: rowsPerPage, skipCount: false }
    postGeneric(apiConstants.NDR_RECORDING_SEARCH, payload)
      .then((res) => {
        if (!on) return
        const recs = res?.data?.data || []
        setRows(recs.map((r) => ({
          awbNumber: r.awb_number,
          merchantName: merchants.find((m) => m.id === r.merchantOid)?.name || r.merchantOid || '—',
          callStartTime: r.call_start_time,
          callDuration: Number(r.call_duration) || 0,
          result: r.ndr_result,
          outcome: r.ndr_outcome,
          provider: r.integration_partner,
          attemptCount: r.attempt_count,
          recordingUrl: r.recording_url,
          callResponses: r.call_responses,
        })))
        setTotal(res?.data?.count ?? recs.length)
      })
      .catch(() => { if (on) { setRows([]); setTotal(0) } })
    return () => { on = false }
  }, [page, rowsPerPage, merchants])

  const recordings = rows.filter(r =>
    !search || r.awbNumber?.toLowerCase().includes(search.toLowerCase()) || r.merchantName?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <Box>
      <Typography variant='h5' sx={{ mb: 2 }}>Call Recordings</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          placeholder='Search by AWB or Merchant...'
          size='small'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position='start'><SearchIcon sx={{ fontSize: 20, color: 'text.disabled' }} /></InputAdornment>,
          }}
          sx={{ width: 360 }}
        />
      </Paper>

      <Paper>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox' />
                <TableCell>AWB</TableCell>
                <TableCell>Merchant</TableCell>
                <TableCell>Date/Time</TableCell>
                <TableCell align='right'>Duration</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell align='center'>Attempt #</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recordings.map((r, i) => (
                <RecordingRow key={r.awbNumber + i} row={r} />
              ))}
              {!recordings.length && (
                <TableRow>
                  <TableCell colSpan={8} align='center' sx={{ py: 4, color: 'text.disabled' }}>
                    No recordings found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component='div'
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0) }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>
    </Box>
  )
}
